/**
 * AI Message Component
 * Renders a single message in the AI chat interface.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AIChartBlock } from './AIChartBlock';
import type { AIMessage, AIToolCall } from '@/types/ai-assistant';

/** Intercepts ```chart fenced blocks and renders them with recharts. */
const markdownComponents = {
  a({ children, ...props }: React.ComponentProps<'a'>) {
    return (
      <a {...props} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  },
  pre({ children, ...props }: React.ComponentProps<'pre'>) {
    const child = (Array.isArray(children) ? children[0] : children) as
      | React.ReactElement<{ className?: string }>
      | undefined;
    if (child && /language-chart/.test(child.props?.className || '')) {
      return <>{children}</>;
    }
    return <pre {...props}>{children}</pre>;
  },
  code({ className, children, ...props }: React.ComponentProps<'code'> & { className?: string }) {
    if (/language-chart/.test(className || '')) {
      return <AIChartBlock source={String(children).trim()} />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

interface AIMessageItemProps {
  message: AIMessage;
  userName?: string;
}

function ToolCallItem({ toolCall }: { toolCall: AIToolCall }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  return (
    <div className="border border-border rounded-md overflow-hidden bg-muted/50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs bg-transparent hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2">
          {toolCall.success ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-destructive" />
          )}
          <span className="font-mono font-medium">{toolCall.tool}</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {toolCall.execution_time_ms}ms
          </Badge>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-3 py-2 border-t border-border bg-background">
          <div className="space-y-2 text-xs">
            <div>
              <span className="text-muted-foreground">Input:</span>
              <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto">
                {JSON.stringify(toolCall.input, null, 2)}
              </pre>
            </div>
            {toolCall.output !== undefined && toolCall.output !== null && (
              <div>
                <span className="text-muted-foreground">Output:</span>
                <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto max-h-32 overflow-y-auto">
                  {typeof toolCall.output === 'string' 
                    ? toolCall.output 
                    : JSON.stringify(toolCall.output, null, 2)}
                </pre>
              </div>
            )}
            {toolCall.error && (
              <div>
                <span className="text-destructive">Error:</span>
                <pre className="mt-1 p-2 bg-destructive/10 rounded text-[10px] text-destructive">
                  {toolCall.error}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AIMessageItem({ message, userName = 'You' }: AIMessageItemProps) {
  const isUser = message.role === 'user';
  const [showToolCalls, setShowToolCalls] = React.useState(false);
  const hasToolCalls = message.tool_calls && message.tool_calls.length > 0;
  
  return (
    <div
      className={cn(
        'flex gap-3 py-4',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {isUser && (
        <Avatar className="h-8 w-8 shrink-0 bg-primary">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
      
      <div className={cn('flex flex-col gap-1 max-w-[80%]', isUser ? 'items-end' : 'items-start')}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {isUser ? userName : 'Sentra AI'}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {new Date(message.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-sm'
              : 'bg-muted rounded-tl-sm'
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none break-words
              prose-p:my-1 prose-p:leading-relaxed
              prose-ul:my-2 prose-ul:pl-4
              prose-ol:my-2 prose-ol:pl-4
              prose-li:my-0.5
              prose-strong:font-semibold
              prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
              prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
              prose-code:bg-background/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
              prose-pre:bg-background/50 prose-pre:p-2 prose-pre:rounded-md prose-pre:overflow-x-auto
              prose-a:text-primary prose-a:underline prose-a:underline-offset-2
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        
        {hasToolCalls && (
          <div className="w-full mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowToolCalls(!showToolCalls)}
              className="text-xs h-7 px-2"
            >
              <Clock className="h-3 w-3 mr-1.5" />
              {message.tool_calls!.length} tool call{message.tool_calls!.length > 1 ? 's' : ''}
              {showToolCalls ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </Button>
            
            {showToolCalls && (
              <div className="mt-2 space-y-2">
                {message.tool_calls!.map((toolCall, index) => (
                  <ToolCallItem key={index} toolCall={toolCall} />
                ))}
              </div>
            )}
          </div>
        )}
        
      </div>
    </div>
  );
}
